import React, { useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { LockClosedIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useDispatch } from "react-redux";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// Components
import GoogleLogin from './GoogleLogin';
import GitHubLogin from './GitHubLoginComp';
import LinkedInLogin from './LinkedInLoginComp';


// Actions and utilities
import { getUser } from "../../store/actions";
import axiosInstance from 'components/Common/ApiManagement/axiosInstance';
import { ApiNames } from '../../components/Common/ApiManagement/apiServices';
import { handleErrors, showToaster } from '../../components/Common/common';
import { PAGEPATHS } from 'components/Common/Constant';

const Login = () => {
  document.title = "Login | GOVCOM - Government Compliance Dashboard";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Please Enter Your Email"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values) => {
      handleSubmit(values);
    }
  });

  const handleSubmit = async (values) => {
    const postForm = {
      email: values.email,
      password: values.password,
      rememberMe
    };

    setLoading(true);

    try {
      const { data } = await axiosInstance.post(ApiNames.USER_SIGNIN, postForm);
      if (data.status === 1) {
        if (data.data) dispatch(getUser(data.data));
        showToaster(data.status, data.message);
        navigate(PAGEPATHS.Scanlist);
      } else {
        showToaster(data.status, data.message);
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative z-10">
          <Link to="/" className="flex items-center space-x-3 text-white">
            {/* <Logo size={50} variant="white" checkColor="#3b82f6" />
            <span className="text-3xl font-bold">GOVCOM™</span> */}
            <img
              src="/images/Logo_high.png"
              alt="GOVCOM Logo"
              className="h-12 w-auto"
            />
          </Link>
        </div>

        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white mb-6">
            Government Compliance<br />Made Simple
          </h1>
          <p className="text-blue-100 text-lg leading-relaxed max-w-md">
            Automated compliance scanning and reporting for government websites across 18 major frameworks.
          </p>

          <div className="mt-12 space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-blue-100">200+ compliance controls across 18 frameworks</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-blue-100">Real-time analysis and instant reports</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <svg className="h-6 w-6 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-blue-100">AI-powered comparative analysis</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-blue-100 text-sm">
          © {new Date().getFullYear()} GOVCOM. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div>
            <Link to="/" className="lg:hidden flex items-center space-x-2 text-blue-600 mb-8">
              {/* <Logo size={42} />
              <span className="text-2xl font-bold">GOVCOM</span> */}
              <img
                src="/images/Logo_low.png"
                alt="GOVCOM Logo"
                className="h-10 w-auto"
              />
            </Link>
            <h2 className="text-3xl font-extrabold text-gray-900">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                Sign up for free
              </Link>
            </p>
          </div>

          <div className="mt-8">
            <form onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }} className="space-y-6">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={validation.values.email || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-3 py-3 border ${validation.touched.email && validation.errors.email
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="you@example.com"
                  />
                </div>
                {validation.touched.email && validation.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={validation.values.password || ""}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    className={`block w-full pl-10 pr-10 py-3 border ${validation.touched.password && validation.errors.password
                      ? 'border-red-300'
                      : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-600 hover:text-gray-800"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {validation.touched.password && validation.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{validation.errors.password}</p>
                )}
              </div>

              {/* Remember & Forgot */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link to={PAGEPATHS.Forget} className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                    Forgot password?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>

            <a
              href={`${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}/user/auth/govengine/start`}
              className="mt-4 w-full flex justify-center py-3 px-4 border border-blue-600 rounded-lg text-sm font-medium text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign in with GovEngine
            </a>

            {/* Google Login */}
            <GoogleLogin />
            <GitHubLogin />
            <LinkedInLogin />

            {/* <button
  type="button"
  className="login-w-g mt-2"
  onClick={() => alert("Login.gov integration will be implemented soon")}
  style={{
    background: "#005EA6",     // 👈 Login.gov primary
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    justifyContent: "center",
    width: "100%",
    padding: "10px",
    borderRadius: "8px",
    border: "none",
    fontWeight: "500",
    transition: "background 0.2s ease"
  }}
  onMouseOver={e => (e.currentTarget.style.background = "#004C87")}  // 👈 hover
  onMouseOut={e => (e.currentTarget.style.background = "#005EA6")}
>
  
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="white"
  >
    <path d="M12 2L2 7v6c0 5 3.8 9.7 10 11 6.2-1.3 10-6 10-11V7l-10-5zm0 2.2l8 4v4.8c0 4-3 7.9-8 9-5-1.1-8-5-8-9V8.2l8-4z" />
    <path d="M11 10h2v6h-2zm0-3h2v2h-2z" />
  </svg>

  <span>Sign in with Login.gov</span>
</button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
